/**
 * Serialize unknown rejection/error values so error emails are identifiable,
 * and detect noise that should not be emailed.
 */

const NOISE_PATTERNS = [
  'ResizeObserver loop completed with undelivered notifications',
  'Non-Error promise rejection captured',
  'Script error',
  'Loading chunk',
  'Loading CSS chunk',
  'ChunkLoadError',
  'Object Not Found Matching Id',
  'MethodName:update',
  'Socket server did not execute the callback',
  'MetaMask',
  'Failed to connect to MetaMask',
  'AbortError',
  'The user aborted a request',
  'The operation was aborted',
  'Failed to fetch',
  'Load failed',
  'NetworkError when attempting to fetch',
  'Minified React error',
  'Looks like your website URL has changed',
  'CookieYes',
  'cdn-cookieyes.com',
]

export interface SerializedError {
  message: string
  details: string
  skip: boolean
}

function isEventLike(value: unknown): value is Event {
  return typeof Event !== 'undefined' && value instanceof Event
}

function isErrorLike(value: unknown): value is Error {
  return (
    value instanceof Error ||
    (typeof value === 'object' &&
      value !== null &&
      'message' in value &&
      typeof (value as { message: unknown }).message === 'string' &&
      'name' in value)
  )
}

function serializeEvent(event: Event): Record<string, unknown> {
  const anyEvent = event as Event & Record<string, unknown>
  const extra: Record<string, unknown> = {
    kind: event.constructor?.name || 'Event',
    type: event.type,
  }

  for (const key of ['message', 'filename', 'lineno', 'colno', 'code', 'reason', 'detail', 'error']) {
    const val = anyEvent[key]
    if (val == null || val === '') continue
    if (isErrorLike(val)) {
      extra[key] = { name: val.name, message: val.message, stack: val.stack }
    } else if (isEventLike(val)) {
      extra[key] = serializeEvent(val)
    } else if (typeof val === 'object') {
      extra[key] = safeJson(val)
    } else {
      extra[key] = val
    }
  }

  const target = event.target as (EventTarget & { tagName?: string; id?: string; src?: string }) | null
  if (target && typeof target === 'object') {
    extra.target = {
      tagName: target.tagName,
      id: target.id || undefined,
      src: target.src || undefined,
    }
  }

  return extra
}

function safeJson(value: unknown): string {
  try {
    const seen = new WeakSet<object>()
    return JSON.stringify(
      value,
      (_key, val) => {
        if (typeof val === 'object' && val !== null) {
          if (seen.has(val)) return '[Circular]'
          seen.add(val)
        }
        if (isErrorLike(val)) {
          return { name: val.name, message: val.message, stack: val.stack }
        }
        if (isEventLike(val)) {
          return serializeEvent(val)
        }
        if (typeof val === 'function') return `[Function ${val.name || 'anonymous'}]`
        return val
      },
      2
    )
  } catch {
    return Object.prototype.toString.call(value)
  }
}

function isGenericEventNoise(serialized: Record<string, unknown>): boolean {
  const kind = String(serialized.kind || '')
  const usefulKeys = Object.keys(serialized).filter(
    (k) => !['kind', 'type', 'target'].includes(k)
  )
  return (
    (kind === 'Event' || kind === 'CustomEvent' || kind === 'PromiseRejectionEvent') &&
    usefulKeys.length === 0
  )
}

export function serializeUnknown(value: unknown): SerializedError {
  if (value == null) {
    return { message: String(value), details: '', skip: true }
  }

  if (typeof value === 'string') {
    const message = value.trim()
    return {
      message: message || '(empty string)',
      details: '',
      skip: !message || isNoiseMessage(message),
    }
  }

  if (isErrorLike(value)) {
    const message = value.message || value.name || 'Error'
    return {
      message,
      details: value.stack || '',
      skip: isNoiseMessage(message),
    }
  }

  if (isEventLike(value)) {
    const serialized = serializeEvent(value)
    const messageParts = [
      `${serialized.kind} type=${serialized.type}`,
      serialized.message ? String(serialized.message) : '',
    ].filter(Boolean)
    const details = JSON.stringify(serialized, null, 2)
    return {
      message: messageParts.join(' — '),
      details,
      skip: isGenericEventNoise(serialized),
    }
  }

  if (typeof value === 'object') {
    const details = safeJson(value)
    const obj = value as Record<string, unknown>
    const message =
      (typeof obj.message === 'string' && obj.message) ||
      (typeof obj.error === 'string' && obj.error) ||
      details.slice(0, 200)
    const tag = Object.prototype.toString.call(value)
    return {
      message,
      details,
      skip:
        tag === '[object Event]' ||
        (tag === '[object Object]' && details === '{}' ) ||
        isNoiseMessage(message),
    }
  }

  return {
    message: String(value),
    details: `typeof: ${typeof value}`,
    skip: isNoiseMessage(String(value)),
  }
}

export function isNoiseMessage(message: string): boolean {
  return NOISE_PATTERNS.some((pattern) => message.includes(pattern))
}

/**
 * Catch-all for emails that would not help diagnose anything,
 * including the historical "Unhandled Promise Rejection: [object Event]" format.
 */
export function isUnidentifiableErrorEmail(subject: string, body: string): boolean {
  const text = `${subject}\n${body}`

  if (isNoiseMessage(text)) return true

  // Preview URLs and headless crawlers — CookieYes/GTM are not registered there.
  if (text.includes('.vercel.app')) return true
  if (text.includes('HeadlessChrome')) return true

  const objectTag = text.match(/\[object (Event|Object|Window|HTMLElement|HTMLUnknownElement)\]/g)
  if (objectTag) {
    const remainder = text
      .replace(/\[object \w+\]/g, '')
      .replace(/Unhandled Promise Rejection:?/gi, '')
      .replace(/Client Error:?/gi, '')
      .replace(/Type:\s*object/gi, '')
      .replace(/URL:\s*\S+/gi, '')
      .replace(/🚨/g, '')
      .replace(/\s+/g, ' ')
      .trim()
    if (remainder.length < 40) return true
  }

  const stripped = text.replace(/\s+/g, ' ').trim()
  if (!stripped || stripped === '🚨 Unhandled Promise Rejection') return true

  return false
}
